import type { TmsFactoringBatchable, TmsInvoiceRow } from '@numeo/types-main-web-app'
import { create } from 'zustand'
import { readCard } from '../api/writeCardApi'

interface FactoringBatchableState {
    /** The company's invoices a new factoring batch takes now (the oldest 50), as the server read them; empty before the first read. */
    rows: TmsInvoiceRow[]
    /** Their whole count, or null before a read succeeded (the offer then counts only the rows on screen). */
    total: number | null
    /**
     * Reads them again. Calls made while a read runs share it, so the tables drawn at once cost one read; `afterWrite` (a write just
     * changed them) reads once more after a running read, which may have started before the write.
     */
    refresh: (tabId: string, afterWrite?: boolean) => Promise<void>
}

let inflight: Promise<void> | null = null

/**
 * The company-wide factoring eligibility the chat's factoring offer counts (S17): an invoice the answer's table does not list is still an
 * invoice a batch takes. Read with `tms_invoices.worklist` `factoring_batchable`, the filter `prepare_batch` groups by. A failed read keeps
 * the last answer (or none): the offer then falls back to the rows on screen, never to a guess.
 */
export const useFactoringBatchableStore = create<FactoringBatchableState>()((set, get) => ({
    rows: [],
    total: null,
    refresh: (tabId, afterWrite = false) => {
        if (inflight) return afterWrite ? inflight.then(() => get().refresh(tabId)) : inflight
        // Started inside the promise, so a read that throws at once is a failed read like any other, never the caller's error.
        inflight = Promise.resolve()
            .then(() => readCard<TmsFactoringBatchable>(tabId, 'tms_invoices.worklist', { factoring_batchable: true }))
            .then(({ factoring_batchable }) => set({ rows: factoring_batchable.rows, total: factoring_batchable.total }))
            .catch(() => undefined)
            .finally(() => {
                inflight = null
            })
        return inflight
    },
}))
